import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "../ui";
import CloseIcon from "@mui/icons-material/Close";
import type { Company } from "@/pages/private/dashboard/SharePage";
import { debtEndpoints, sharePageEndpoints } from "@/shared/api/endpoints";
import { CustomerSharingResponse } from "@/shared/api/types";
import { httpDelete, httpGet, httpPut } from "@/shared/api/http";
import { ThemeColours } from "@/clients/acme/config/colours";
import { ApiResponse } from "@/pushApi";
import { DebtsService } from "@/shared/api/services";

interface DeleteCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  company: Company;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
}

const DeleteCompanyDialog: React.FC<DeleteCompanyDialogProps> = ({
  open,
  onClose,
  company,
  setCompanies,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);

    if (
      !company.isFp &&
      (company.raw?.lastSharedDate || company.raw?.submitDate) &&
      company.raw?.isSharing == "Y"
    ) {
      await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerSharing(),
        {
          ...company.raw,
          isSharing: "N",
        }
      );

      setCompanies((prev) =>
        prev.map((c) => {
          if (c.id == company.id) {
            c.raw.isSharing = "N";
          }

          return c;
        })
      );

      onClose();

      setIsSaving(false);

      return;
    }

    if (
      company.isFp &&
      company.raw?.submitDate &&
      company.raw?.isSharing == "Y"
    ) {
      await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createFreepostSharing(),
        {
          ...company.raw,
          isSharing: "N",
        }
      );

      setCompanies((prev) =>
        prev.map((c) => {
          if (c.id == company.id) {
            c.raw.isSharing = "N";
          }

          return c;
        })
      );

      onClose();

      setIsSaving(false);

      return;
    } else if (company.isFp) {
      await httpDelete<ApiResponse>(
        sharePageEndpoints.getFreePostCustomerSharing(company.id)
      );
    } else {
      // delete payment offer here
      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string
      );

      const lenderName = company.raw?.lenderName;
      const accountNumber = company.raw?.accountNumber;
      const customerId = customerData.customerId;

      const response = await httpGet<ApiResponse>(
        debtEndpoints.paymentOffer.getSingle(
          customerId,
          lenderName as string,
          accountNumber as string
        )
      );

      const data: any = response?.data;

      console.log(lenderName, accountNumber, customerId, data);

      if (data.length > 0) {
        await DebtsService.deletePaymentOffer(data[0].id, {
          skipErrorCapture: true,
        });
      }

      await httpDelete<ApiResponse>(
        sharePageEndpoints.getCustomerSharing(company.id)
      );
    }

    setCompanies((prev) => prev.filter((c) => c.id != company.id));
    onClose();

    setIsSaving(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "15.025px",
          boxShadow: "0px 15.025px 45.074px 0px rgba(0,0,0,0.15)",
          px: "22px",
          py: 0,
          m: 2,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: "18px",
              borderBottom: "1.502px solid rgba(68, 72, 109, 0.1)",
            }}
          >
            <Typography
              sx={{
                fontSize: "22px",
                fontWeight: 600,
                color: "#000",
                lineHeight: "20.952px",
              }}
            >
              Delete Company
            </Typography>
            <IconButton
              onClick={onClose}
              disabled={isSaving}
              sx={{
                backgroundColor: "#e5e7eb",
                borderRadius: "32px",
                padding: "8px",
                width: "40px",
                height: "40px",
                "&:hover": {
                  backgroundColor: "#d1d5db",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: "24px", color: "#000" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              pt: "16.152px",
              pb: "24px",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, color: ThemeColours.subtitleVar1 }}
            >
              Are you sure you want to delete this from share page?
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: "22.537px",
                width: "100%",
              }}
            >
              <Button
                onClick={onClose}
                disabled={isSaving}
                sx={{
                  flex: 1,
                  height: "45.074px",
                  borderRadius: "10px",
                  color: "#1f2937",
                  fontSize: "18px",
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "#f3f4f6",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "#e5e7eb",
                    border: "none",
                  },
                  "&:disabled": {
                    backgroundColor: "#f3f4f6",
                    color: "#9ca3af",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  flex: 1,
                  height: "45.074px",
                  backgroundColor: "#EF4444",
                  borderRadius: "10px",
                  color: "#fff",
                  fontSize: "18px",
                  fontWeight: 600,
                  textTransform: "none",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "#FF0F17",
                    border: "none",
                  },
                  "&:disabled": {
                    backgroundColor: "#8300047a",
                    color: "#fff",
                  },
                }}
              >
                {isSaving ? "Delete..." : "Delete"}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCompanyDialog;
